import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { audiencesService } from "../../services/audiences";
import { getPlatformIcon } from "../../utils/platforms";
import { formatNumber } from "../../utils/numbers";
import CreatorDetailModal from "../../components/modals/CreatorDetailModal";
import FitScoreRadial from "../../components/audience/FitScoreRadial";
import SortableTh from "../../components/common/SortableTh";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import { getTotalPages, parsePaginatedResponse } from "../../utils/pagination";
import { smoothScrollAudiencePageTop } from "../../utils/scroll";
import MiniDistribution from "../../components/patterns/MiniDistribution";

const PAGE_SIZE = 20;

const TOOLTIP = {
  followers: "Follower count from the platform for this creator.",
  videos:
    "Count of short-form videos on this channel. This is a total count, not an average across videos.",
  views:
    "Average views per short-form video on this channel. The mini chart places this average next to the distribution of every short-form video from all creators on this page.",
  likes:
    "Average likes per short-form video on this channel. The mini chart uses all short-form videos from creators on this page.",
  comments:
    "Average comments per short-form video on this channel. The mini chart uses all short-form videos from creators on this page.",
  engagement:
    "Average engagement rate per short-form video: mean of (likes + comments) / views for each video with views. The mini chart uses the same video pool as other columns on this page.",
  fit: "How well this creator matches the audience category, when the audience has a category and a fit score exists.",
  recentVideo:
    "Upload date of the most recent short-form video we have for this channel. Sort uses this date.",
  synced: "Last time we synced this creator from the platform.",
};

const formatAvgCount = (num) => {
  if (num == null || Number.isNaN(Number(num))) return null;
  const n = Number(num);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n === 0) return null;
  if (n >= 1000) return formatNumber(n);
  if (n >= 100) return String(Math.round(n));
  return n.toLocaleString(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 0 });
};

const formatEngagementPct = (rate) => {
  if (rate == null || !Number.isFinite(Number(rate))) return null;
  const n = Number(rate);
  if (n === 0) return null;
  return `${n.toFixed(2)}%`;
};

const CreatorMetricWithChart = ({ value, distribution, display, showChart, title }) => (
  <td className="px-4 py-3 whitespace-nowrap text-right" title={title}>
    <div className="flex items-center justify-end gap-2 min-w-0">
      {display != null && display !== "" ? (
        <span className="text-sm text-theme-text shrink-0 tabular-nums">{display}</span>
      ) : (
        <span className="text-sm text-theme-text opacity-40 shrink-0">--</span>
      )}
      {showChart ? (
        <div className="shrink-0 flex items-end pt-0.5" aria-hidden>
          <MiniDistribution distribution={distribution} patternMedian={value} compact />
        </div>
      ) : null}
    </div>
  </td>
);

const CreatorRow = ({ item, onRowClick, pageDistributions, showFitColumn }) => {
  const creator = item.creator;
  const m = item.list_metrics || {};
  const platformIcon = getPlatformIcon(creator.platform);
  const hasRichData = (creator.name && creator.name !== creator.platform_id) || creator.profile_image_url;
  const [imageError, setImageError] = useState(false);

  const videoCount = m.video_count ?? 0;
  const avgViews = m.avg_views;
  const avgLikes = m.avg_likes;
  const avgComments = m.avg_comments;
  const engagementNum = m.engagement_rate != null ? Number(m.engagement_rate) : null;

  const dist = pageDistributions || {};
  const showViewsChart = Boolean(dist.views?.length && avgViews != null && avgViews > 0);
  const showLikesChart = Boolean(dist.likes?.length && avgLikes != null && avgLikes > 0);
  const showCommentsChart = Boolean(dist.comments?.length && avgComments != null && avgComments > 0);
  const showEngagementChart = Boolean(
    dist.engagement?.length
      && engagementNum != null
      && !Number.isNaN(engagementNum)
      && engagementNum !== 0
      && videoCount > 0
  );

  return (
    <tr
      className="hover:bg-theme-secondary transition-colors cursor-pointer"
      onClick={() => onRowClick(item)}
    >
      <td className="px-4 py-3 max-w-[280px] w-[280px]">
        <div className="flex items-center gap-3 min-w-0">
          {hasRichData && creator.profile_image_url && !imageError ? (
            <img
              src={creator.profile_image_url}
              alt={creator.name || creator.platform_id}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-10 h-10 bg-theme-secondary rounded-full flex items-center justify-center text-theme-text opacity-60 flex-shrink-0">
              {platformIcon ? (
                <div className="w-6 h-6">{React.cloneElement(platformIcon, { className: "w-6 h-6" })}</div>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              )}
            </div>
          )}
          <div className="min-w-0 flex-1">
            {hasRichData && creator.name ? (
              <>
                <div className="text-sm font-medium text-theme-text truncate" title={creator.name}>
                  {creator.name}
                </div>
                <div className="text-xs text-theme-text opacity-60 truncate" title={creator.platform_id}>
                  {creator.description?.trim() ? creator.description : creator.platform_id}
                </div>
              </>
            ) : (
              <div className="text-sm font-medium text-theme-text truncate" title={creator.platform_id}>
                {creator.platform_id}
              </div>
            )}
          </div>
        </div>
      </td>

      <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-theme-text tabular-nums" title={TOOLTIP.followers}>
        {creator.follower_count != null && creator.follower_count > 0 ? (
          formatNumber(creator.follower_count)
        ) : (
          <span className="text-theme-text opacity-40">--</span>
        )}
      </td>

      <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-theme-text tabular-nums" title={TOOLTIP.videos}>
        {videoCount > 0 ? (
          String(videoCount)
        ) : (
          <span className="text-theme-text opacity-40">--</span>
        )}
      </td>

      <CreatorMetricWithChart
        value={avgViews}
        distribution={dist.views}
        display={formatAvgCount(avgViews)}
        showChart={showViewsChart}
        title={TOOLTIP.views}
      />
      <CreatorMetricWithChart
        value={avgLikes}
        distribution={dist.likes}
        display={formatAvgCount(avgLikes)}
        showChart={showLikesChart}
        title={TOOLTIP.likes}
      />
      <CreatorMetricWithChart
        value={avgComments}
        distribution={dist.comments}
        display={formatAvgCount(avgComments)}
        showChart={showCommentsChart}
        title={TOOLTIP.comments}
      />
      <CreatorMetricWithChart
        value={engagementNum}
        distribution={dist.engagement}
        display={formatEngagementPct(engagementNum)}
        showChart={showEngagementChart}
        title={TOOLTIP.engagement}
      />

      {showFitColumn ? (
        <td className="px-4 py-3 whitespace-nowrap text-center" title={TOOLTIP.fit}>
          {item.category_fit?.fit_score != null ? (
            <FitScoreRadial score={item.category_fit.fit_score} />
          ) : (
            <span className="text-sm text-theme-text opacity-40">--</span>
          )}
        </td>
      ) : null}

      <td
        className="px-4 py-3 whitespace-nowrap text-right text-xs text-theme-text opacity-80 tabular-nums"
        title={TOOLTIP.recentVideo}
      >
        {creator.most_recent_shortform_video_at ? (
          new Date(creator.most_recent_shortform_video_at).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        ) : (
          <span className="text-theme-text opacity-40">--</span>
        )}
      </td>

      <td
        className="px-4 py-3 whitespace-nowrap text-right text-xs text-theme-text opacity-80 tabular-nums"
        title={TOOLTIP.synced}
      >
        {creator.synced_at ? (
          new Date(creator.synced_at).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        ) : (
          <span className="text-theme-text opacity-40">--</span>
        )}
      </td>
    </tr>
  );
};

const Creators = ({ audience }) => {
  const navigate = useNavigate();
  const { creatorHash } = useParams();
  const [creators, setCreators] = useState([]);
  const [pageMeta, setPageMeta] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [prevPageUrl, setPrevPageUrl] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("best");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sizeFilter, setSizeFilter] = useState("");

  const baseUrl = `/audience/${audience?.hash}/creators/`;

  const showFitColumn = useMemo(
    () => creators.some((c) => c.category_fit?.fit_score != null),
    [creators]
  );

  const creatorsWithFitFlag = useMemo(
    () => creators.map((c) => ({ ...c, _showFit: showFitColumn })),
    [creators, showFitColumn]
  );

  const pageDistributions = useMemo(
    () => ({
      views: pageMeta?.videos?.views?.distribution ?? null,
      likes: pageMeta?.videos?.likes?.distribution ?? null,
      comments: pageMeta?.videos?.comments?.distribution ?? null,
      engagement: pageMeta?.videos?.engagement_rate?.distribution ?? null,
    }),
    [pageMeta]
  );

  const handleRowClick = (item) => {
    navigate(`/audience/${audience?.hash}/creators/${item.hash || item.creator?.hash}/`);
  };

  const handleSort = (sortKey) => {
    setPage(1);
    if (sortKey === sortBy) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(sortKey);
      setSortDirection("desc");
    }
  };

  const fetchCreators = useCallback(() => {
    if (!audience?.hash) {
      setCreators([]);
      setPageMeta(null);
      setTotalCount(0);
      setNextPageUrl(null);
      setPrevPageUrl(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const params = {
      audience_hash: audience.hash,
      sort_by: sortBy,
      sort_direction: sortDirection,
      page,
      page_size: PAGE_SIZE,
    };
    if (search) params.search = search;
    if (sizeFilter) params.size = sizeFilter;

    audiencesService.getAudienceCreators(
      params,
      (response) => {
        const { results, count, next, previous, meta } = parsePaginatedResponse(response);
        setCreators(results);
        setPageMeta(meta);
        setTotalCount(count);
        setNextPageUrl(next);
        setPrevPageUrl(previous);
        setLoading(false);
      },
      () => {
        setCreators([]);
        setPageMeta(null);
        setTotalCount(0);
        setNextPageUrl(null);
        setPrevPageUrl(null);
        setLoading(false);
      }
    );
  }, [audience?.hash, page, search, sizeFilter, sortBy, sortDirection]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [audience?.hash, sortBy, sizeFilter]);

  useEffect(() => {
    fetchCreators();
  }, [fetchCreators]);

  const handleRefresh = () => {
    fetchCreators();
  };

  const totalPages = getTotalPages(totalCount, PAGE_SIZE);
  const showPagination = creators.length > 0 && totalPages > 1;

  const sortThProps = {
    sortBy,
    sortDirection,
    onSort: handleSort,
    showSortArrow: false,
    activeClassName: "patterns-sort-th-active",
  };

  if (loading && creators.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <OrbitalLoader size={128} />
      </div>
    );
  }

  if (creators.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-theme-text opacity-80 mb-4">
          <svg
            className="w-16 h-16 mx-auto mb-4 opacity-50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-theme-text mb-2">
          {search || sizeFilter ? "No creators match these filters" : "No creators yet"}
        </h3>
        <p className="text-theme-text opacity-60 mb-4">
          {search || sizeFilter
            ? "Try a different keyword or size bucket."
            : "Creators are being populated for this audience. This may take a few moments."}
        </p>
        <button
          onClick={() => {
            setSearchInput("");
            setSizeFilter("");
            setPage(1);
            setSearch("");
          }}
          className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90"
        >
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-theme-text">Creators ({totalCount})</h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search creators"
            className="px-3 py-2 bg-theme-secondary border border-theme-secondary rounded-md text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
          />
          <select
            value={sizeFilter}
            onChange={(e) => setSizeFilter(e.target.value)}
            className="px-3 py-2 bg-theme-secondary border border-theme-secondary rounded-md text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
          >
            <option value="">All sizes</option>
            <option value="mongo">Mongo (10M+)</option>
            <option value="mega">Mega (1M+)</option>
            <option value="macro">Macro (100K-1M)</option>
            <option value="mid">Mid (10K-100K)</option>
            <option value="micro">Micro (1K-10K)</option>
            <option value="nano">Nano (&lt;1K)</option>
          </select>
          <button
            onClick={handleRefresh}
            className="px-3 py-2 bg-theme-secondary border border-theme-secondary rounded-md text-theme-text hover:opacity-80"
            title="Refresh"
            type="button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>
      </div>

      <div
        className={`rounded-lg border border-theme-secondary bg-theme-main overflow-hidden transition-opacity ${loading ? "opacity-60 pointer-events-none" : ""}`}
        aria-busy={loading}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-theme-secondary border-b border-theme-secondary">
                <th className="px-4 py-2 text-left text-xs font-medium text-theme-text uppercase tracking-wider max-w-[280px] w-[280px]">
                  Creator
                </th>
                <SortableTh
                  {...sortThProps}
                  label="Followers"
                  tooltip={TOOLTIP.followers}
                  sortKey="followers"
                  align="right"
                />
                <SortableTh
                  {...sortThProps}
                  label="Videos"
                  tooltip={TOOLTIP.videos}
                  sortKey="videos"
                  align="right"
                />
                <SortableTh {...sortThProps} label="Views" tooltip={TOOLTIP.views} sortKey="views" align="right" />
                <SortableTh {...sortThProps} label="Likes" tooltip={TOOLTIP.likes} sortKey="likes" align="right" />
                <SortableTh
                  {...sortThProps}
                  label="Comments"
                  tooltip={TOOLTIP.comments}
                  sortKey="comments"
                  align="right"
                />
                <SortableTh
                  {...sortThProps}
                  label="Engagement"
                  tooltip={TOOLTIP.engagement}
                  sortKey="engagement"
                  align="right"
                />
                {showFitColumn ? (
                  <SortableTh {...sortThProps} label="Fit" tooltip={TOOLTIP.fit} sortKey="fit_score" />
                ) : null}
                <SortableTh
                  {...sortThProps}
                  label="Recent video"
                  tooltip={TOOLTIP.recentVideo}
                  sortKey="recent_video"
                  align="right"
                />
                <SortableTh {...sortThProps} label="Synced" tooltip={TOOLTIP.synced} sortKey="synced_at" align="right" />
              </tr>
            </thead>
            <tbody className="bg-theme-main divide-y divide-theme-secondary">
              {creators.map((item) => (
                <CreatorRow
                  key={item.hash}
                  item={item}
                  onRowClick={handleRowClick}
                  pageDistributions={pageDistributions}
                  showFitColumn={showFitColumn}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showPagination ? (
        <div className="flex items-center justify-between pt-1">
          <p className="text-xs text-theme-text opacity-70">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setPage((value) => Math.max(1, value - 1));
                smoothScrollAudiencePageTop();
              }}
              disabled={!prevPageUrl || loading}
              className="px-3 py-1.5 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => {
                setPage((value) => value + 1);
                smoothScrollAudiencePageTop();
              }}
              disabled={!nextPageUrl || loading}
              className="px-3 py-1.5 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}

      {creatorHash && (
        <CreatorDetailModal
          audience={audience}
          creatorHash={creatorHash}
          baseUrl={baseUrl}
          pageCreators={creators}
        />
      )}
    </div>
  );
};

export default Creators;
